import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AvConfirmDialog,
  AvDialog,
  AvTooltip,
  ButtonTypes,
  ColumnReorder,
  DateBucketValueType,
  DescriptorType,
  DimTypes,
  ExportButton,
  ExpressionTypes,
  FeatureFlags,
  FieldListObject,
  FieldTypes,
  getUniqueFieldNames,
  HistoricalFilterModeWarning,
  Illustration,
  isTopKFilter,
  NoDataFound,
  NOT_EXPLODE_OPTION,
  removeMetricsFromFilter,
  RepeatedDimType,
  Select,
  TextInput,
} from '@fabric-ui/components';
import {
  FileFormat,
  FormatterType,
  ReportModal,
  SortedTimeBucketOptions,
  TimeBucket,
  TimeBucketName,
  TimeBucketOrder,
  TimeBucketToDateGap,
} from '@fabric-ui/components/Table';
import { Filter as FilterIcon, QuestionFull, Search } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { DateFormats } from '@fabric-ui/utils';
import { Box, Divider, LinearProgress, Stack, SvgIcon, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import { keepPreviousData } from '@tanstack/react-query';
import { Block } from '@tanstack/react-router';
import { getParamFromURL } from '@views/CustomDashboards/components/Clickthrough/utils';
import {
  checkIsNoneDateBucket,
  getExpression,
  getFieldTitleByExpression,
  getFieldTypeByExpression,
  getSimplifiedDateTruncExpression,
  getSimplifiedExplodeExpression,
} from '@views/CustomDashboards/CustomDashboardsUtils/expressionUtils';
import { useExportWidget } from '@views/CustomDashboards/hooks/useExportWidget';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { z } from 'zod/v4';
import AvFilters from '../../components/AvFilters';
import { AvHistoricalDatePicker, newDefaultTimeRange } from '../../components/AvHistoricalDatePicker';
import AvProjTable from '../../components/AvProjTable';
import FieldsToolbar from '../../components/FieldsToolbar';
import { StylePages } from '../../components/FieldsToolbar/useStyle';
import { useAvContext } from '../../context/AvContextProvider';
import { NavigationBlockerModal } from '../../context/NavigationBlockerProvider';
import { useQueryObjectAsync } from '../../hooks/queryObjectAsync';
import { useAllApps } from '../../hooks/useAllApps';
import { useCustomSearchParams } from '../../hooks/UseCustomSearchParams2';
import { useGetUsersInfo } from '../../hooks/useGetUsersInfo';
import useQueryObject from '../../hooks/useQueryObjectSql';
import { APP_PATHS, PAGE_PATHS } from '../../types';
import { useNavigate, useParams } from '../../utils/AvRouter';
import { expressionToFilter, filterToExpression } from '../../utils/filterUtils';
import {
  deserializeSortItem,
  getDimsWithAlias,
  getSelectedFieldsData,
  parseDims,
  parseDimsWithExpression,
  parseWidgetDataBeforeApiRequest,
  serializeSortItem,
  sortByStringField,
} from '../../utils/utils';
import { useAuditNavigation } from '../AuditLogs/hooks';
import DashboardToolbar from '../CustomDashboards/components/DashboardToolbar';
import { getKeyByValue } from '../CustomDashboards/components/SelectWidgetType/SelectWidgetType';
import { inputInnerLabelStartAdornment } from '../CustomDashboards/CustomDashboardsUtils';
import { HistoricalFilterModeName } from '../CustomDashboards/EditCustomDashboard/Tabs/WidgetFilters/WidgetFilters';
import { hasTimeBucket } from '../CustomDashboards/EditCustomDashboard/TimeBucketBreakdown/utils';
import { useEditDashboards } from '../CustomDashboards/hooks';
import { useDashboard } from '../CustomDashboards/hooks/useDashboard';
import { useGetDisplayName } from '../CustomDashboards/hooks/useGetDisplayName';
import { useGetFieldType } from '../CustomDashboards/hooks/useGetFieldType';
import {
  AllTypeWidgets,
  CustomDashboardDto,
  DashboardReportPermission,
  DwQueryRequest,
  HistoricalFilterMode,
  PermissionType,
  ReportsTypes,
  TableRowSize,
  TableSubType,
  TableTypeWidget,
  UserPermissionType,
  WidgetCategory,
} from '../CustomDashboards/types';
import { FieldType } from '../CustomDashboards/types/types';
import { getHeadCellsWithSort } from '../Tickets/hooks';
import { filtersFieldToIgnore } from '../Tickets/ticket.types';
import { getScheduleExportReportPermissions } from './EditReports/hooks';
import EntityItem, { DragItemEntity } from './EntityItem';
import ExportDialogButton from './ExportPopper/ExportDialogButton';
import { getSearchParamsFromQueryObject, useBuildQueryParams, useCalculatedDimensionsList, useMeasurementsList } from './hooks';
import { useDataQuery } from './hooks/useDataQuery';
import {
  convertURIToValue,
  convertValueToURI,
  FieldsToFilter,
  getHeadCellsMeasurements,
  getHeadCellsWithoutMeasurements,
  getNewDeliveryConfig,
} from './reports.utils';
import SaveAsNewDialog from './SaveAsNewDialog';
import { defaultExportDetails, ExportDetails } from './types';
import { useReportPermissions } from './useReportPermissions';

const ENABELD_FIELD_TYPES_WITH_OPTIONS = new Set([FieldTypes.Measurement, FormatterType.date, FormatterType.array]);

const dataFieldDropStyle = (theme, active) => ({
  border: '2px dashed',
  borderColor: 'var(--color-border-base-secondary)',
  color: 'var(--color-content-base-primary)',
  ...(active && {
    borderColor: 'var(--color-border-interactive-primary-default)',
    color: 'var(--color-content-base-primary)',
  }),
});

const FieldsFilterType = { InUse: 1, Available: 2 };
const onDragOver = e => e.preventDefault();
export const defaultFilter = {
  tablePage: 0,
  activeProjName: 'tickets',
  headCells: [],
  orderBy: [],
  isEditMode: true,
  isCurrentTime: true,
  expressions: {},
  historicalFilterMode: HistoricalFilterMode.CURRENT,
};

const collapseProps = { unmountOnExit: true, timeout: 200 };
const rowLimit = 30;
const exportLimit = 100000;
const defaultModalData: ReportModal = { tempProjName: '', isOpen: false };

export default function EditReports({
  controlledProjName,
  isPreviewMode = false,
  onQueryChange,
  enableNewComponent = true,
}: {
  controlledProjName?: string;
  isPreviewMode?: boolean;
  onQueryChange?: (query: DwQueryRequest) => void;
  enableNewComponent?: boolean;
}) {
  const theme: any = useTheme();
  const {
    getPathName,
    featureFlags,
    userPermissions: { hasAllowedEditResourcePermission },
    accountData,
    accountEntities: { aggProjs },
    selectedApp,
    hasAppInAccount,
    measurements: rawMeasurements,
  } = useAvContext();

  const {
    flags: { isRecommendedFieldsEnabled },
  } = accountData;

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const [isDirty, setIsDirty] = useState(false);
  const dirtyAction = React.useRef(<T extends (...args: any[]) => any>(func: T) => {
    function action(...args: Parameters<T>) {
      setIsDirty(true);
      // @ts-expect-error: expected
      func.apply(this, args);
    }
    return action as T;
  });

  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const reportsHistorical = featureFlags[FeatureFlags.ReportsHistorical];
  const { data: allApps = [], isLoading: isAllAppsLoading } = useAllApps();
  const selectedAppName = allApps.find(({ name }) => selectedApp === APP_PATHS[name])?.name;
  const [filters, setFilters] = useCustomSearchParams();

  const expressions = getParamFromURL<{ [key: string]: { name: string; function: string; bucket: DateBucketValueType; alias: string } }>(
    filters.expressions
  );

  const historicalFilterMode = featureFlags[FeatureFlags.WidgetHistoricalFilterMode]
    ? z.enum(HistoricalFilterMode).default(HistoricalFilterMode.CURRENT).parse(getParamFromURL<string>(filters.historicalFilterMode))
    : HistoricalFilterMode.CURRENT;

  const setHistoricalFilterMode = (mode: HistoricalFilterMode) => {
    setFilters({ ...filters, historicalFilterMode: mode });
  };

  const [name, setName] = useState('New Report');
  const [tags, setTags] = useState<string[]>([]);
  const [exportDetails, setExportDetails] = useState<ExportDetails>(defaultExportDetails);
  const [dashboardReportPermission, setDashboardReportPermission] = useState<DashboardReportPermission>({
    viewers: [],
    editors: [],
    type: PermissionType.private,
  });
  const [userPermissionType, setUserPermissionType] = useState<UserPermissionType>(UserPermissionType.editor);
  const [dragItem, setDragItem] = useState({});
  const [changeEntityDialogValue, setChangeEntityDialogValue] = useState<ReportModal>(defaultModalData);
  const [app, setApp] = useState<string[]>(selectedAppName && selectedApp && selectedApp !== APP_PATHS.EXPLORE ? [selectedAppName] : []);
  const [expandedEntities, setExpandedEntities] = useState<Record<string, boolean>>({
    measurements: false,
  });
  const [entitiesInUseFilter, setEntitiesInUseFilter] = useState<number>();
  const [entitiesSearch, setEntitiesSearch] = useState('');
  const [isSaveAsNewDialogOpen, setIsSaveAsNewDialogOpen] = useState(false);
  const parsedFieldDropRef = useRef(undefined);
  const activeProjName = useMemo(() => controlledProjName || getParamFromURL<string>(filters.activeProjName), [filters.activeProjName]);
  const isCurrentTime = getParamFromURL<boolean>(filters.isCurrentTime);

  const schedulerOwnerId = useMemo(() => exportDetails.deliveryConfig?.schedulerOwner, [exportDetails.deliveryConfig?.schedulerOwner]);
  const { usersInfo: schedulerOwnerInfo } = useGetUsersInfo(schedulerOwnerId ? [schedulerOwnerId] : [], data => data);

  const { notAllowedIds } = useReportPermissions(id ? [id] : []);
  const idsWithoutPermissions = useMemo(() => new Set(notAllowedIds), [notAllowedIds]);

  const { data: canEditScheduleExport } = featureFlags[FeatureFlags.ScheduleExportPermissions]
    ? getScheduleExportReportPermissions({
        reportId: id,
        enabled: !isAllAppsLoading && !isPreviewMode && !!id,
      })
    : { data: true };

  const isAllowedEdit =
    hasAllowedEditResourcePermission({
      path: PAGE_PATHS.REPORTS,
      isNew: !id,
    }) && userPermissionType !== UserPermissionType.viewer;

  const getHeadCellType = useGetFieldType(activeProjName);

  const isEditMode = getParamFromURL<boolean>(filters.isEditMode) && isAllowedEdit;
  const page = getParamFromURL<number>(filters.tablePage);

  const { name: entityName } = aggProjs[activeProjName]?.entityTypeId ?? {};

  const { data: measurements = [] } = useMeasurementsList({
    entityName,
    filterFunc: v => (isCurrentTime ? v.expression && v.context.includes(entityName) : v.timeExpression && v.context.includes(entityName)),
  });
  const { data: calculatedDimensions = [] } = useCalculatedDimensionsList({
    projectionName: aggProjs[activeProjName]?.projId.name,
    filterFunc: v => v.context?.some(ctx => ctx.name === aggProjs[activeProjName]?.projId.name),
    enabled: !!aggProjs[activeProjName],
  });
  const typeMap = useMemo(
    () =>
      [...measurements, ...calculatedDimensions].reduce(
        (acc, { systemName, type }) => ({ ...acc, [systemName]: type }),
        aggProjs[activeProjName]?.typeMap ?? {}
      ),
    [measurements, calculatedDimensions, activeProjName]
  );

  const orderBy = useMemo(
    () => filters.orderBy.map(deserializeSortItem).filter(({ property }) => !filters.headCells || filters.headCells.includes(property)),
    [filters]
  );

  const getDisplayName = useGetDisplayName(aggProjs[activeProjName].projId.name);

  const headCells = useMemo(
    () =>
      getHeadCellsWithSort(
        filters.headCells.map(cell => {
          if (expressions[cell]) {
            const expression = getExpression(cell, expressions[cell].function, expressions[cell].bucket) as ExpressionTypes;
            return {
              id: cell,
              ...(expressions[cell] && {
                type: getFieldTypeByExpression(expression),
                label: getFieldTitleByExpression({ expression, displayName: getDisplayName(cell) }),
                fieldName: expressions[cell].bucket,
              }),
            };
          }
          return { id: cell };
        }),
        orderBy
      ),
    [filters.headCells, orderBy]
  );

  const headCellsIds = useMemo(() => headCells.map(({ id }) => id), [headCells]);
  const timeBucket = useMemo(() => headCellsIds.find(v => Object.keys(TimeBucket).includes(v)), [headCellsIds, TimeBucket]);

  const getFieldDisplayNameById = ({ fieldList }, fieldId) =>
    fieldList.INTERACTIVE.find(({ value }) => value === fieldId)?.title ||
    measurements.find(({ systemName }) => systemName === fieldId)?.displayName ||
    calculatedDimensions.find(({ systemName }) => systemName === fieldId)?.displayName ||
    TimeBucketName[fieldId] ||
    fieldId;

  const { data = {}, isLoading } = useDashboard({
    id,
    onSuccess: (dashboardData: CustomDashboardDto) => {
      const data = {
        ...dashboardData,
        widgets: dashboardData.widgets.map(widget => ({
          ...widget,
          requests: widget.requests.map(request => ({
            ...request,
            select: { ...request.select, dims: parseDimsWithExpression(request.select.dims || []) },
          })),
        })),
      };

      const { name, active, apps, widgets, intervalExpression, deliveryConfig, dashboardReportPermission, userPermissionType, tags } = data;

      setName(name);
      setTags(tags || []);
      const request = widgets[0].requests[0];
      const { historicalFilterMode } = widgets[0];
      const updatedActiveProjName = request.projectionId.name;
      setApp(allApps.filter(({ name }) => apps && apps.includes(name)).map(({ name }) => name));
      if (dashboardReportPermission) {
        setDashboardReportPermission(dashboardReportPermission);
      }
      if (userPermissionType) {
        setUserPermissionType(userPermissionType);
      }

      // AVA-18764: we need to calculate the measurements based on the projectionId in the response.
      const updatedEntityTypeId = aggProjs[updatedActiveProjName]?.entityTypeId?.name;
      const filteredMeasurements = sortByStringField(
        rawMeasurements.visible.filter(v =>
          isCurrentTime
            ? v.expression && v.context.includes(updatedEntityTypeId)
            : v.timeExpression && v.context.includes(updatedEntityTypeId)
        ),
        'displayName'
      );

      const typeMap = [...filteredMeasurements, ...calculatedDimensions].reduce(
        (acc, { systemName, type }) => ({ ...acc, [systemName]: type }),
        aggProjs[request.projectionId.name]?.typeMap ?? {}
      );

      const requestFilters = {
        ...(request.filter ? expressionToFilter(typeMap, request.filter) : {}),
        ...getSearchParamsFromQueryObject(request),
      };

      const localFiltersWithoutEmpty = Object.fromEntries(
        Object.entries(filters).filter(([key, item]) =>
          filtersFieldToIgnore.includes(key) ? false : !(key in requestFilters) || (Array.isArray(item) && item.length > 0)
        )
      );

      const mergedFilters = {
        ...requestFilters,
        ...localFiltersWithoutEmpty,
        historicalFilterMode: historicalFilterMode || HistoricalFilterMode.CURRENT,
      };

      setFilters(mergedFilters);
      setExportDetails({
        intervalExpression,
        enabled: active,
        deliveryConfig,
      });

      return data;
    },
    onError: errors => {
      if (errors[0].extensions.status === 403) {
        enqueueSnackbar(errors[0].message, { variant: 'error' });
        goBack();
      }
    },
    enabled: !isAllAppsLoading && !isPreviewMode,
  });
  const displayName = data?.name || name;
  const omitAliasPrefix = data?.omitAliasPrefix;

  const updateFilters = (key, val, isPageChange = false) => {
    const newFilters = { ...filters, [key]: val, ...(isPageChange ? {} : { tablePage: 0 }) };
    setFilters(newFilters);
  };

  const clearAllFilters = dirtyAction.current(() => {
    setFilters({
      ...defaultFilter,
      activeProjName,
      orderBy: filters.orderBy,
      headCells: filters.headCells,
    });
  });

  const initHeadCells = headCells =>
    activeProjName
      ? [
          ...headCells.reduce(
            (obj, headcell) => [
              ...obj,
              {
                id: headcell.id,
                sort: headcell.sort,
                ...(Object.keys(TimeBucket).includes(headcell.id) && {
                  formatter: v => (v ? format(new Date(v), DateFormats[TimeBucketToDateGap[headcell.id]]) : v),
                }),
                ...headcell,
              },
            ],
            []
          ),
        ]
      : [];

  const tableHeadCells = useMemo(() => initHeadCells(headCells), [headCells]);

  const handleExpand = projName => setExpandedEntities(prev => ({ ...prev, [projName]: !prev[projName] }));
  const entitiesObject = aggProjs[activeProjName].fieldList.INTERACTIVE.reduce((acc, item) => {
    const key = item.group.split(' Custom Fields')[0];
    if (FieldsToFilter.includes(item.systemName)) {
      return acc;
    }
    return { ...acc, [key]: [...(acc[key] || []), item] };
  }, {});

  const getVisibleEntityFields = (entity: FieldListObject[]) =>
    entity.filter(field => {
      const isInUseFilterResult = fieldValue => {
        if (entitiesInUseFilter) {
          return entitiesInUseFilter === FieldsFilterType.InUse ? headCellsIds.includes(fieldValue) : !headCellsIds.includes(fieldValue);
        }
        return true;
      };
      return (
        (!field.visibilityConfig || !field.visibilityConfig?.config?.hidden) &&
        field.title.toLowerCase().includes(entitiesSearch.toLowerCase()) &&
        isInUseFilterResult(field.value)
      );
    });

  const handleRemoveColumn = dirtyAction.current(fieldName => {
    // eslint-disable-next-line custom-rules/no-let
    let { orderBy } = filters;
    // When time bucket is removed, get the next smallest one
    if (hasTimeBucket(fieldName)) {
      const smallestTimeBucket = headCellsIds
        .filter((field: string) => field !== fieldName && hasTimeBucket(field))
        .sort((timeBucket1, timeBucket2) => TimeBucketOrder[timeBucket1] - TimeBucketOrder[timeBucket2])[0];
      orderBy = smallestTimeBucket ? [serializeSortItem({ property: smallestTimeBucket, isAsc: false })] : [];
    }

    const { [fieldName]: currentExpression, ...restExpressions } = expressions;

    setFilters({
      ...filters,
      orderBy,
      headCells: headCellsIds.filter(field => field !== fieldName),
      activeProjName,
      tablePage: 0,
      expressions: restExpressions,
    });
  });

  const getNewOrderBy = (fieldName: string) => {
    if (headCells.length) {
      if (hasTimeBucket(fieldName)) {
        const smallestTimeBucketSelected = [...filters.headCells, fieldName]
          .filter(dim => hasTimeBucket(dim))
          .sort((timeBucket1, timeBucket2) => TimeBucketOrder[timeBucket1] - TimeBucketOrder[timeBucket2])[0];
        // Always return the smallest timebucket selected
        return smallestTimeBucketSelected === filters.orderBy[0]?.split('__')?.[0]
          ? filters.orderBy
          : [serializeSortItem({ property: fieldName, isAsc: false })];
      }
      return filters.orderBy;
    }

    return [serializeSortItem({ property: fieldName, isAsc: false })];
  };

  const handleDrop = dirtyAction.current((dragItem: DragItemEntity | Record<string, never>) => {
    const fieldName = dragItem.value;
    if (headCellsIds.includes(fieldName)) {
      handleRemoveColumn(fieldName);
    } else {
      setFilters({
        ...filters,
        orderBy: getNewOrderBy(fieldName),
        headCells: addHeadCell(fieldName),
      });
    }

    setDragItem({});
  });

  const addHeadCell = fieldName => {
    const measurementsSystemNames = measurements.map(({ systemName }) => systemName);
    const isMeasurement = measurementsSystemNames.includes(fieldName);
    if (isMeasurement) {
      return [...headCellsIds, fieldName];
    }
    const findMeasurementIndex = headCellsIds.findIndex(field => measurementsSystemNames.includes(field));
    if (findMeasurementIndex !== -1) {
      return headCellsIds.flatMap((id, index) => {
        if (index < findMeasurementIndex) {
          return [id];
        }
        if (index === findMeasurementIndex) {
          return [fieldName, id];
        }
        return [id];
      });
    }

    return [...headCellsIds, fieldName];
  };
  const goBack = (ignoreBlocker?: boolean) => navigate({ ...getPathName(PAGE_PATHS.REPORTS), ignoreBlocker });

  const headCellsWithoutMeasurements = getHeadCellsWithoutMeasurements(headCellsIds, rawMeasurements.asObject);
  const headCellsMeasurements = getHeadCellsMeasurements(headCellsIds, rawMeasurements.asObject);

  const dataQuery = useDataQuery({
    headCellsWithoutMeasurements,
    headCellsMeasurements,
    expressions,
    orderBy,
    typeMap,
    isCurrentTime,
    historicalFilterMode,
    activeProjName,
    page,
    rowLimit,
  });

  useEffect(() => {
    onQueryChange?.(dataQuery);
  }, [JSON.stringify(dataQuery)]);

  const parseQueryObject = dataQuery => ({
    ...dataQuery,
    select: { ...dataQuery.select, dims: parseDims(dataQuery.select.dims) },
  });

  const dimsWithAlias = getDimsWithAlias(dataQuery.select.dims);
  const parseRows = rows => {
    const parsedData = rows.map(item => {
      const parsedItem = Object.keys(item).reduce((acc, key) => ({ ...acc, [dimsWithAlias[key]?.name || key]: item[key] }), {});

      return parsedItem;
    });

    return parsedData;
  };

  const {
    data: rows = [],
    totals,
    isLoading: isLoadingData,
    isRefetching,
    totalRowCount,
  } = useQueryObject({
    queryObject: parseQueryObject(dataQuery),
    showTotals: isCurrentTime && !!headCellsMeasurements.length,
    totalRowCount: true,
    options: {
      placeholderData: keepPreviousData,
      enabled: !reportsHistorical && (isCurrentTime || headCellsIds.filter((headCell: string) => !hasTimeBucket(headCell)).length > 0),
    },
    onSuccess: parseRows,
  });

  const {
    data: asyncRows = [],
    isLoading: isLoadingHistory,
    isRefetching: isRefetchingHistory,
    totals: totalsHistory,
    totalRowCount: totalRowCountHistory,
    // TODO: refactor to use useQueryObjectAsyncWithCachedResult
  } = useQueryObjectAsync({
    queryObject: parseQueryObject(dataQuery),
    showTotals: isCurrentTime && !!headCellsMeasurements.length,
    options: {
      placeholderData: keepPreviousData,
      enabled: reportsHistorical && (isCurrentTime || headCellsIds.filter((headCell: string) => !hasTimeBucket(headCell)).length > 0),
    },
    totalRowCount: true,
    debounceQueries: false,
    onSuccess: parseRows,
  });
  const isLoadingRefetch = reportsHistorical ? isLoadingHistory || isRefetchingHistory : isRefetching || isLoadingData;
  const count = reportsHistorical ? totalRowCountHistory : totalRowCount;
  const totalsData = reportsHistorical ? totalsHistory : totals;
  const finalRows = useMemo(() => (reportsHistorical ? asyncRows : rows), [rows, asyncRows, measurements]);

  const setReportPopperData = dirtyAction.current((info: Partial<CustomDashboardDto>) => {
    setName(info.name!);
    if (info.tags !== undefined) {
      setTags(info.tags);
    }
    if (info.apps) {
      setApp(info.apps);
    }
    if (info.dashboardReportPermission) {
      setDashboardReportPermission(info.dashboardReportPermission);
    }
  });

  const metricsWithAliases = useBuildQueryParams({
    headCellsIds: measurements.map(({ systemName }) => systemName),
    measurements,
  });

  const { mutate: save, isPending: isSaving } = useEditDashboards({
    parseData: widget => {
      const parsedWidget = parseWidgetDataBeforeApiRequest({ widget, isSavingWidget: true, metricsWithAliases });

      if (
        featureFlags[FeatureFlags.WidgetHistoricalFilterMode] &&
        historicalFilterMode === HistoricalFilterMode.HISTORICAL &&
        !isCurrentTime
      ) {
        parsedWidget.historicalFilterMode = HistoricalFilterMode.HISTORICAL;
      } else {
        parsedWidget.historicalFilterMode = undefined;
      }

      return {
        ...parsedWidget,
        requests: parsedWidget.requests.map(request => ({ ...request, top: undefined })),
      };
    },
    onSuccess: (isRerun, id, shouldGoBack, isCreate) => {
      setIsDirty(false);
      enqueueSnackbar(`Successfully ${isCreate ? 'created' : 'saved'} ${isRerun ? 'and ran' : ''} report`, {
        variant: 'success',
      });
      if (shouldGoBack) {
        goBack(true);
      } else if (isCreate) {
        navigate({ ...getPathName(PAGE_PATHS.REPORTS, `/edit/${id}`), ignoreBlocker: true });
      }
    },
  });

  const headCellsIdLabel = headCells.map(cell => ({
    id: cell.id,
    label: getFieldDisplayNameById(aggProjs[activeProjName], cell.id),
  }));

  const { exportWidget, isExporting } = useExportWidget();

  const onColumnPickerApply = items => setFilters({ ...filters, headCells: items.map(({ id }) => id) });
  const handleClose = () => setChangeEntityDialogValue(defaultModalData);

  const onSort = dirtyAction.current((property, direction) => {
    const isAsc = direction === 'asc';
    const nextOrderBy = [
      ...orderBy.filter(item => item.property !== property).slice(0, 0),
      ...(direction
        ? [
            {
              property,
              isAsc,
            },
          ]
        : []),
    ];
    updateFilters('orderBy', nextOrderBy.map(serializeSortItem));
  });

  const setEntitiesFilter = (type: number) => setEntitiesInUseFilter(entitiesInUseFilter === type ? undefined : type);

  const measurementsEntities = measurements.map(({ displayName, systemName }) => ({
    title: displayName,
    value: systemName,
  }));

  const calculatedDimensionsEntities = calculatedDimensions.map(({ displayName, systemName }) => ({
    title: displayName,
    value: systemName,
  }));

  const calculatedDimensionsForFilters = useMemo(
    () =>
      !isCurrentTime
        ? []
        : calculatedDimensions.map(({ displayName, systemName, type }) => ({
            value: systemName,
            pathAlias: systemName,
            title: displayName,
            group: 'Calculated Dimensions',
            entityTypeId: { builtin: true },
            type: type?.kindWrapper?.kind ? DescriptorType[type.kindWrapper.kind] : type,
          })),
    [calculatedDimensions, isCurrentTime]
  );

  const hasRiskAppInAccount = useMemo(() => hasAppInAccount({ appPath: APP_PATHS.RISK360 }), [accountData]);

  const handleEntityChange = newProjName => {
    if (filters.headCells.length) {
      setChangeEntityDialogValue({ tempProjName: newProjName, isOpen: true });
    } else {
      setFilters({
        ...defaultFilter,
        activeProjName: newProjName,
        isCurrentTime,
      });
    }
  };

  const acceptEntityChange = (selectedProjName: string) =>
    setFilters({
      ...defaultFilter,
      activeProjName: selectedProjName,
      isCurrentTime,
    });

  const checkIfToOpenDialog = (selectedProjName: string) => {
    if (!filters.headCells.length) {
      acceptEntityChange(selectedProjName);
      return false;
    }
    return true;
  };

  const disableSave =
    !isAllowedEdit ||
    !name ||
    (isCurrentTime ? !headCells.length : headCells.filter(headCell => !hasTimeBucket(headCell)).length < 1) ||
    isSaving;

  const tableWidget: TableTypeWidget = {
    requests: [dataQuery],
    definition: {
      coords: { x: 0, y: 0, h: 0, w: 0 },
      title: { title: name },
      custom: {
        rowSize: TableRowSize.Small,
      },
    },
    category: WidgetCategory.Table,
    type: TableSubType.Table,
  };
  const reportsDto: CustomDashboardDto = {
    ...(id ? { id } : {}),
    type: ReportsTypes.TABULAR,
    name,
    widgets: [tableWidget],
    intervalExpression: exportDetails.intervalExpression,
    deliveryConfig: getNewDeliveryConfig(exportDetails.deliveryConfig),
    customQueries: null,
    active: exportDetails.enabled,
    apps: allApps.filter(({ name }) => app.includes(name)).map(({ name }) => name),
    dashboardReportPermission,
    omitAliasPrefix,
    tags,
  };
  const customDashboard: CustomDashboardDto = {
    ...reportsDto,
    widgets: [{ ...reportsDto.widgets[0], requests: [{ ...dataQuery, top: undefined }] } as AllTypeWidgets],
  };

  const { menuOptions } = useAuditNavigation();

  const metricsForFilters = useMemo(
    () =>
      historicalFilterMode === HistoricalFilterMode.HISTORICAL
        ? []
        : measurements.map(({ systemName, displayName, type, isRecommended }) => ({
            value: systemName,
            pathAlias: systemName,
            title: displayName,
            group: FieldTypes.Measurement,
            entityTypeId: { builtin: true },
            type,
            isRecommended,
          })),
    [measurements, historicalFilterMode]
  );

  const columnReorder = (
    <ColumnReorder
      disabled={!headCells.length || (!isCurrentTime && headCells.length === 1)}
      items={headCellsIdLabel}
      setItems={onColumnPickerApply}
      initialColumns={headCellsIdLabel}
      sortOnly
    />
  );
  const isValidReport = (headCells.length && isCurrentTime) || (!isCurrentTime && headCells.length > 1);

  const getDateExpressions = (field, selectedValue, restExpressions) =>
    checkIsNoneDateBucket(selectedValue)
      ? restExpressions
      : { ...restExpressions, ...getSimplifiedDateTruncExpression(field.value, selectedValue) };

  const getExpressionsForURL = (type, field, selectedValue, restExpressions) => {
    if (type === DimTypes.Date) {
      return getDateExpressions(field, selectedValue, restExpressions);
    }

    if (type === RepeatedDimType.String) {
      if (selectedValue === NOT_EXPLODE_OPTION.value) {
        return restExpressions;
      }
      return getSimplifiedExplodeExpression(field.value);
    }
    return restExpressions;
  };

  const onApplyExpression = ({ field, type, selectedValue }) => {
    const { [field.value]: currentExpression, ...restExpressions } = expressions;

    const newExpressions = getExpressionsForURL(type, field, selectedValue, restExpressions);

    setFilters({
      ...filters,
      expressions: newExpressions,
    });
  };

  const onChangeFilterMode = v => {
    const parsedFilters = filterToExpression(typeMap, filters);
    if (parsedFilters && v === HistoricalFilterMode.HISTORICAL) {
      const fieldNames = Array.from(getUniqueFieldNames(parsedFilters)) as string[];
      const hasMetricFilter = fieldNames.some(fieldName => rawMeasurements.asObject[fieldName]);
      const hasCalculatedDimensionFilter = fieldNames.some(fieldName =>
        calculatedDimensions.some(calcDim => calcDim.systemName === fieldName)
      );

      if (hasMetricFilter || hasCalculatedDimensionFilter) {
        setIsPopupOpen(true);
        return;
      }
    }

    setHistoricalFilterMode(v);
  };

  const onSwitchToHistoricalFilter = () => {
    const nonFieldProperties = Object.keys(filters).reduce((acc, key) => (!typeMap[key] ? { ...acc, [key]: filters[key] } : acc), {});

    const expression = filterToExpression(typeMap, filters);
    const newFilterExpression = removeMetricsFromFilter(expression, rawMeasurements.asObject);

    // Also remove calculated dimensions from filter by creating a calculated dimensions object
    const calculatedDimensionsAsObject = calculatedDimensions.reduce(
      (acc, calcDim) => ({
        ...acc,
        [calcDim.systemName]: { name: calcDim.systemName },
      }),
      {}
    );
    const filterWithoutCalcDims = newFilterExpression ? removeMetricsFromFilter(newFilterExpression, calculatedDimensionsAsObject) : null;
    const parsedFilter = expressionToFilter(typeMap, filterWithoutCalcDims);

    // Also remove calculated dimensions from headCells
    const calculatedDimensionNames = calculatedDimensions.map(calcDim => calcDim.systemName);
    const headCellsWithoutCalcDims = (filters.headCells || []).filter(headCell => !calculatedDimensionNames.includes(headCell));

    setFilters({
      ...nonFieldProperties,
      ...parsedFilter,
      headCells: headCellsWithoutCalcDims,
      historicalFilterMode: HistoricalFilterMode.HISTORICAL,
    });
    setIsPopupOpen(false);
  };

  return (
    <>
      <Block withResolver enableBeforeUnload={isDirty} shouldBlockFn={({ current, next }) => isDirty && current.pathname !== next.pathname}>
        {({ proceed, status, reset }) =>
          reset && proceed ? <NavigationBlockerModal onClose={reset} onDiscard={proceed} open={status === 'blocked'} /> : null
        }
      </Block>
      <Box className="fluid-container" sx={{ ...flex.col, height: '100%' }}>
        {!isPreviewMode && (
          <DashboardToolbar
            title="Report"
            newData={{
              name: displayName,
              tags,
              apps: app,
              dashboardReportPermission,
            }}
            onSuccess={setReportPopperData}
            onCancel={() => goBack()}
            filters={filters}
            setFilters={setFilters}
            disabled={!isAllowedEdit}
            menuOptions={id ? menuOptions : undefined}
            isLoading={isLoading && !!id}
            toolbarButtons={[
              {
                label: 'Done',
                disabled: disableSave,
                clickHandler: () => save({ customDashboard, isCreate: !id }),
                isEditModeOnly: true,
              },
            ]}
            saveButtonsProps={{
              label: 'Save',
              disabled: disableSave,
              options: [
                { label: 'Save', clickHandler: () => save({ customDashboard, isCreate: !id, shouldGoBack: false }), groupNumber: 1 },
                {
                  label: 'Save & Run',
                  clickHandler: () => save({ isRerun: true, customDashboard, isCreate: !id, shouldGoBack: false }),
                  groupNumber: 1,
                },
                ...(id ? [{ label: 'Save As New', clickHandler: () => setIsSaveAsNewDialogOpen(true), groupNumber: 1 }] : []),
              ],
            }}
          />
        )}
        <SaveAsNewDialog
          save={save}
          isSaveAsNewDialogOpen={isSaveAsNewDialogOpen}
          customDashboard={customDashboard}
          setIsSaveAsNewDialogOpen={setIsSaveAsNewDialogOpen}
        />
        <Box sx={{ ...flex.col, gap: 1, height: '100%', overflow: 'auto', ...(!isPreviewMode && { px: 4, pt: 1, pb: 2 }) }}>
          <Box sx={{ ...flex.col, gap: 1 }}>
            <Box sx={{ ...flex.justifyBetweenCenter }}>
              {featureFlags[FeatureFlags.WidgetHistoricalFilterMode] && !isCurrentTime ? (
                <Stack direction="row" justifyItems="flex-start" alignItems="center" gap={0.5}>
                  <Select
                    showClearSelection={false}
                    size="xSmall"
                    options={Object.values(HistoricalFilterMode).map(v => ({ title: HistoricalFilterModeName[v], value: v }))}
                    value={historicalFilterMode || HistoricalFilterMode.CURRENT}
                    onChange={onChangeFilterMode}
                    muiProps={{ disableClearable: true }}
                    style={{ width: '128px' }}
                  />
                  <AvTooltip
                    muiProps={{ placement: 'top' }}
                    title={
                      <span>
                        Filter Evaluation Time:
                        <br />
                        Apply filters either to the current state of the data or to its historical state at the time of the query
                      </span>
                    }>
                    <SvgIcon component="span" sx={{ width: 18, height: 18, ...flex.center }}>
                      <QuestionFull style={{ width: 18, height: 18 }} viewBox="0 0 20 20" />
                    </SvgIcon>
                  </AvTooltip>
                  <Divider sx={{ mx: 1 }} orientation="vertical" flexItem />
                </Stack>
              ) : null}

              <AvConfirmDialog
                isOpen={isPopupOpen}
                title="Measurement Filter Will Be Removed"
                body={
                  <span>
                    Measurement filtering is only available in the current state.
                    <br />
                    Switching to historical mode will remove your applied filters,
                    <br />
                    would you like to proceed?
                  </span>
                }
                confirmButton={{
                  text: 'Yes',
                  onClick: onSwitchToHistoricalFilter,
                }}
                onClose={() => setIsPopupOpen(false)}
                closeButton={{ onClick: () => setIsPopupOpen(false) }}
              />

              <Box sx={{ ...flex.row, mr: 'auto' }}>
                <AvFilters
                  key={activeProjName}
                  updateFilters={dirtyAction.current((key, val) => updateFilters(key, val))}
                  setFilters={dirtyAction.current(newFilters => setFilters(newFilters))}
                  filters={filters}
                  onClearFilters={dirtyAction.current(() => {
                    const { headCells, activeProjName } = filters;
                    setFilters({ headCells, activeProjName });
                  })}
                  activeProjName={activeProjName}
                  dims={aggProjs[activeProjName].fieldList.INTERACTIVE}
                  metrics={metricsForFilters}
                  calcDims={calculatedDimensionsForFilters}
                  isFilterExpression
                  fieldsListAdornment={
                    historicalFilterMode === HistoricalFilterMode.HISTORICAL && <HistoricalFilterModeWarning sx={{ mt: 1 }} />
                  }
                  showAdvanced={isCurrentTime}
                />
              </Box>
              <Box sx={{ ...flex.center, gap: 2, alignSelf: 'flex-start' }}>
                {!isPreviewMode && (
                  <AvHistoricalDatePicker
                    isEditMode={isEditMode && reportsHistorical}
                    isCurrentTime={isCurrentTime}
                    shouldWarn={headCellsMeasurements.length > 0}
                    setIsCurrentTime={dirtyAction.current(v => {
                      if (!v) {
                        const calculatedDimensionNames = new Set(calculatedDimensions.map(calcDim => calcDim.systemName));
                        const headCellsWithoutCalcDims = headCellsWithoutMeasurements.filter(
                          headCell => !calculatedDimensionNames.has(headCell)
                        );
                        const isTopKValue = (val: unknown) => {
                          if (!Array.isArray(val) || val.length !== 1) return false;
                          try {
                            return isTopKFilter(JSON.parse(decodeURIComponent(val[0]))?.type);
                          } catch {
                            return false;
                          }
                        };
                        const filtersWithoutTopK = Object.fromEntries(Object.entries(filters).filter(([, val]) => !isTopKValue(val)));
                        setFilters({
                          ...filtersWithoutTopK,
                          orderBy: [],
                          headCells: headCellsWithoutCalcDims,
                          isCurrentTime: false,
                          timeRange: convertValueToURI(newDefaultTimeRange),
                        });
                        setExpandedEntities({ ...expandedEntities, timeBucket: true });
                      } else {
                        const { timeRange, ...rest } = filters;
                        setFilters({
                          ...rest,
                          orderBy: [],
                          headCells: headCellsWithoutMeasurements.filter(v => !hasTimeBucket(v)),
                          isCurrentTime: true,
                        });
                      }
                    })}
                    timeRange={filters.timeRange ? convertURIToValue(filters.timeRange) : newDefaultTimeRange}
                    updateFilters={dirtyAction.current(newTimeRange => updateFilters('timeRange', convertValueToURI(newTimeRange)))}
                  />
                )}
                {reportsHistorical && !isPreviewMode && <Divider orientation="vertical" variant="middle" sx={{ height: 24 }} flexItem />}
                {!isPreviewMode ? (
                  <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
                    <AvTooltip title="Save the report to enable scheduling" disabled={!!id}>
                      <div>
                        <ExportDialogButton
                          disabled={!id}
                          initExportDetails={exportDetails}
                          setInitExportDetails={v => setExportDetails(v)}
                          formatOptions={[
                            FileFormat.CSV_FORMAT,
                            FileFormat.JSON_FORMAT,
                            FileFormat.EXCEL_FORMAT,
                            ...(hasRiskAppInAccount ? [FileFormat.R360_JSON_FORMAT] : []),
                          ]}
                          dashboardReportId={id!}
                          allowEdit={isAllowedEdit && canEditScheduleExport && !!id && !idsWithoutPermissions.has(id)}
                          isCompressAvailable
                          type={customDashboard.type}
                          scheduledBy={schedulerOwnerInfo?.[0]}
                          {...((!canEditScheduleExport || (!!id && idsWithoutPermissions.has(id))) && {
                            warningMessage: "You don't have permissions to edit this scheduling settings. Please contact your admin.",
                          })}
                        />
                      </div>
                    </AvTooltip>
                    <Box sx={{ ...flex.itemsCenter }}>
                      <ExportButton
                        disabled={!headCells.length || (!isCurrentTime && headCells.length === 1)}
                        loading={isExporting}
                        onClick={() => {
                          const { top, ...rest } = dataQuery;
                          exportWidget({ widget: { ...tableWidget, requests: [rest] }, globalFilters: {} });
                        }}
                        exportLimit={exportLimit}
                        totalRowsCount={count}
                      />
                      {columnReorder}
                    </Box>
                  </Box>
                ) : (
                  columnReorder
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ ...flex.row, gap: 1.5, height: '100%', overflow: 'auto', overflowX: 'hidden' }}>
            {isEditMode &&
              (enableNewComponent ? (
                <Box sx={{ ...flex.col, gap: 2, flexShrink: 0, width: '314px' }}>
                  <FieldsToolbar
                    key={activeProjName}
                    activeProjectionName={activeProjName}
                    changeEntity={{
                      enableEntityChange: !controlledProjName,
                      checkIfToOpenDialog,
                      acceptEntityChange,
                      popupText: 'Changing the main report entity will reset fields selection, would you like to proceed',
                    }}
                    selectFields={{
                      onFieldClick: handleDrop,
                      selectedFields: getSelectedFieldsData(dataQuery.select.dims, dataQuery.select.metrics),
                      onApplyExpression,
                    }}
                    defaultExpandedEntities={{
                      [getKeyByValue(FieldType, FieldType.Measurements) || '']: true,
                      [aggProjs[activeProjName].projDisplayName]: true,
                    }}
                    isHistoricalActive={!isCurrentTime}
                    hiddenButtons={
                      new Set([
                        ButtonTypes.All,
                        ...(isRecommendedFieldsEnabled && featureFlags[FeatureFlags.TopFields] && featureFlags[FeatureFlags.PopulatedFields]
                          ? []
                          : [ButtonTypes.TopFields, ButtonTypes.Populated]),
                      ])
                    }
                    page={StylePages.reports}
                    enabledFieldTypesWithOptions={ENABELD_FIELD_TYPES_WITH_OPTIONS}
                  />
                </Box>
              ) : (
                <Box sx={{ ...flex.col, gap: 2, flexShrink: 0, width: '314px' }}>
                  <div>
                    {!controlledProjName && (
                      <Select
                        startAdornment={inputInnerLabelStartAdornment('Main Entity', 'small')}
                        muiProps={{ disableClearable: true }}
                        size="small"
                        value={activeProjName}
                        onChange={handleEntityChange}
                        getValueFunc={(projName: any) => projName}
                        getLabelFunc={(projName: any) => aggProjs[projName].projDisplayName}
                        options={Object.keys(aggProjs).sort() as any}
                      />
                    )}
                    <AvDialog
                      open={changeEntityDialogValue.isOpen}
                      onClose={handleClose}
                      title="Change Main Entity"
                      footer={
                        <Box sx={{ ...flex.justifyEndCenter, gap: 2 }}>
                          <Button onClick={handleClose}>Cancel</Button>
                          <Button
                            variant="contained"
                            onClick={() => {
                              setFilters({
                                ...defaultFilter,
                                activeProjName: changeEntityDialogValue.tempProjName,
                                isCurrentTime,
                                ...(isCurrentTime ? [] : { headCells: [timeBucket] }),
                              });
                              handleClose();
                            }}>
                            Yes
                          </Button>
                        </Box>
                      }>
                      <div>Changing the main report entity will reset fields selection, would you like to proceed</div>
                    </AvDialog>
                  </div>
                  <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
                    <FilterIcon
                      style={{
                        color: entitiesSearch || entitiesInUseFilter ? theme.palette.primary.main : 'var(--color-content-base-secondary)',
                      }}
                    />
                    <Button
                      variant="filter"
                      size="xSmall"
                      onClick={() => setEntitiesFilter(FieldsFilterType.InUse)}
                      className={entitiesInUseFilter === FieldsFilterType.InUse ? 'active-filter' : undefined}>
                      In Use
                    </Button>
                    <Button
                      variant="filter"
                      size="xSmall"
                      onClick={() => setEntitiesFilter(FieldsFilterType.Available)}
                      className={entitiesInUseFilter === FieldsFilterType.Available ? 'active-filter' : undefined}>
                      Available
                    </Button>
                    <Box
                      sx={{
                        pl: 4,
                        fontStyle: 'italic',
                        fontWeight: 400,
                        color: 'var(--color-content-base-tertiary)',
                        fontSize: '12px',
                      }}>
                      {headCells.length} Added
                    </Box>
                  </Box>
                  <TextInput
                    size="small"
                    helperText="Search..."
                    icon={<Search />}
                    value={entitiesSearch}
                    onChange={v => setEntitiesSearch(v.toLowerCase())}
                  />
                  <Box sx={{ ...flex.col, gap: '12px', overflowY: 'auto' }}>
                    {!isCurrentTime && reportsHistorical ? (
                      <EntityItem
                        displayName="Date Breakdown"
                        expanded={expandedEntities['Date Breakdown']}
                        setExpand={handleExpand}
                        onItemClick={handleDrop}
                        onDragStart={setDragItem}
                        items={SortedTimeBucketOptions.map(({ label, value }) => ({ title: label, value }))}
                        collapseProps={collapseProps}
                        usedFields={headCellsIds}
                      />
                    ) : null}
                    {Object.keys(entitiesObject).map(key => {
                      if (entitiesObject[key][0]) {
                        const {
                          group,
                          entityTypeId: { builtIn },
                        } = entitiesObject[key][0];
                        const displayName = builtIn ? group : group.replace('uber_', '');
                        return (
                          <EntityItem
                            key={key}
                            displayName={displayName}
                            onItemClick={dragItem => handleDrop(dragItem)}
                            onDragStart={setDragItem}
                            expanded={expandedEntities[displayName]}
                            collapseProps={collapseProps}
                            setExpand={handleExpand}
                            items={getVisibleEntityFields(entitiesObject[key])}
                            usedFields={headCellsIds}
                          />
                        );
                      }
                      return undefined;
                    })}
                    {measurements.length ? (
                      <EntityItem
                        key="measurements"
                        displayName="measurements"
                        onItemClick={dragItem => handleDrop(dragItem)}
                        onDragStart={setDragItem}
                        expanded={expandedEntities.measurements}
                        collapseProps={collapseProps}
                        setExpand={handleExpand}
                        items={getVisibleEntityFields(measurementsEntities)}
                        usedFields={headCellsIds}
                        type={FieldTypes.Measurement}
                      />
                    ) : null}
                    {calculatedDimensions.length ? (
                      <EntityItem
                        key="calculatedDimensions"
                        displayName="Calculated Dimensions"
                        onItemClick={dragItem => handleDrop(dragItem)}
                        onDragStart={setDragItem}
                        expanded={expandedEntities.calculatedDimensions}
                        collapseProps={collapseProps}
                        setExpand={handleExpand}
                        items={getVisibleEntityFields(calculatedDimensionsEntities)}
                        usedFields={headCellsIds}
                        type={FieldTypes.CalculatedDimension}
                      />
                    ) : null}
                  </Box>
                </Box>
              ))}

            <Box
              sx={[
                {
                  ...flex.col,
                  width: '100%',
                  height: '100%',
                  overflowY: 'auto',
                  position: 'relative',
                },
                isValidReport || enableNewComponent ? {} : dataFieldDropStyle(theme, Object.keys(dragItem)?.length),
              ]}
              ref={parsedFieldDropRef}
              onDragOver={onDragOver}
              onDrop={() => handleDrop(dragItem)}>
              {isLoadingRefetch && <LinearProgress sx={{ position: 'absolute', width: '100%' }} />}
              {headCells.filter(({ id }) => !hasTimeBucket(id)).length > 0 ? (
                <Box
                  sx={{
                    '&, .MuiTableCell-head': {
                      background: 'var(--color-surface-base-primary)',
                    },
                    py: 1.5,
                    px: 1.5,
                    height: '100%',
                    overflowY: 'hidden',
                  }}>
                  {isLoadingRefetch && !isCurrentTime ? (
                    <Box sx={{ ...flex.col, px: 1, gap: 0.5 }}>
                      <div>Preparing your historical analytics report...</div>
                      <div>This may take a minute or two as we shift through vast amounts of data</div>
                    </Box>
                  ) : count !== 0 ? (
                    <AvProjTable
                      headCellsKey="id"
                      activeProjName={activeProjName}
                      sx={{ height: count !== 0 ? '100%' : 'auto' }}
                      headCells={tableHeadCells}
                      rowLimit={rowLimit}
                      enableSelectionColor={false}
                      hasHeaderFilters={false}
                      loading={isLoadingRefetch}
                      size="xSmall"
                      rows={finalRows}
                      onSort={onSort}
                      allowEdit={false}
                      totalProps={
                        totalsData && headCellsMeasurements.length
                          ? {
                              row: headCellsIds.reduce(
                                (acc, id) => ({
                                  ...acc,
                                  [id]:
                                    totalsData[id] === null ||
                                    totalsData[id] === undefined ||
                                    (totalsData[id] === 0 && getHeadCellType({ entityFieldKey: id }) === FormatterType.number)
                                      ? '-'
                                      : totalsData[id],
                                }),
                                {}
                              ),
                              cells: tableHeadCells,
                            }
                          : {}
                      }
                      resizable
                      pagination
                      onCancelColumn={handleRemoveColumn}
                      paginationProps={{
                        onPageChange: page => updateFilters('tablePage', page, true),
                        totalRows: count,
                        pageNum: page,
                      }}
                      shouldPreferLabelAsCellTitle
                    />
                  ) : (
                    <NoDataFound
                      size="responsive"
                      headline="No data Found"
                      callToAction={
                        <>
                          Try to{' '}
                          <Button variant="primaryText" onClick={clearAllFilters}>
                            Clear All filters
                          </Button>
                        </>
                      }
                    />
                  )}
                </Box>
              ) : (
                <NoDataFound
                  headline="Add query fields to get started!"
                  img={<Illustration.Medium.Setup />}
                  callToAction={enableNewComponent ? '' : 'Drag fields or Click to add'}
                  size="responsive"
                  style={{ callToActionStyle: { color: 'var(--color-content-base-secondary)' } }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
